import { ChangeEvent, FC, memo, useCallback, useEffect, useState } from 'react';
import './calendar-header.css';
import moment from 'moment';
import Select from '../../Select/Select';
import { Timestamp } from '../types';

type Props = {
  title?: string;
  monthTimestamp: number;
  isDisabled?: boolean;
  setDropDownValue: (monthTimestamp: Timestamp) => void;
  startYearOffset?: number;
};

const CalendarHeader: FC<Props> = (props) => {
  const { title, isDisabled, monthTimestamp, setDropDownValue, startYearOffset = 100 } = props;

  const date = new Date(monthTimestamp);
  const month = date.toLocaleDateString('en', { month: 'long' });
  const monthNumber = String(date.getMonth() + 1).padStart(2, '0'); // Get month as number (01-12)
  const [monthValue, setMonthValue] = useState(monthNumber);
  const [yearValue, setYearValue] = useState(String(date.getFullYear()));

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      if (isDisabled) {
        return;
      }
      if (e.target.name === 'month') {
        setMonthValue(e.target.value);
      } else {
        setYearValue(e.target.value);
      }
    },
    [isDisabled],
  );

  useEffect(() => {
    const format = `${yearValue}-${monthValue}-15`;
    setDropDownValue(moment(format).valueOf() as Timestamp);
  }, [monthValue, yearValue]);

  useEffect(() => {
    const currentMonthNumber = String(date.getMonth() + 1).padStart(2, '0');
    const currentYear = String(date.getFullYear());

    // Only update if the external monthTimestamp changed (not from our own update)
    if (monthValue !== currentMonthNumber || yearValue !== currentYear) {
      setMonthValue(currentMonthNumber);
      setYearValue(currentYear);
    }
  }, [monthTimestamp]); // Changed dependency to monthTimestamp instead of month

  const MonthOptions = [
    { label: 'Jan', value: '01' },
    { label: 'Feb', value: '02' },
    { label: 'Mar', value: '03' },
    { label: 'Apr', value: '04' },
    { label: 'May', value: '05' },
    { label: 'Jun', value: '06' },
    { label: 'Jul', value: '07' },
    { label: 'Aug', value: '08' },
    { label: 'Sep', value: '09' },
    { label: 'Oct', value: '10' },
    { label: 'Nov', value: '11' },
    { label: 'Dec', value: '12' },
  ];
  const generateYearOptions = (): Record<string, string> => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - startYearOffset; // default: 100 years back
    const endYear = currentYear + 15; // 15 years forward

    const yearOptions: Record<string, string> = {};
    for (let year = startYear; year <= endYear; year += 1) {
      yearOptions[String(year)] = String(year); // Ensure keys are strings
    }

    return yearOptions;
  };
  const YearOptions = generateYearOptions();
  return (
    <div className="calendar-header">
      {title ? (
        <div>
          {' '}
          <span className="calendar-header-year-bold">{title}</span>
        </div>
      ) : (
        <div>
          {month} <span className="calendar-header-year">{date.getFullYear()}</span>
        </div>
      )}
      <div className="calendar-header-switcher">
        <Select
          variant="calendar-header"
          options={MonthOptions}
          key="month"
          name="month"
          value={monthValue}
          placeholderColor="#2D2D2D"
          placeholder={monthValue}
          noOrdering
          onBlur={() => {}}
          onChange={(e) => handleChange(e)}
        />
        <div style={{ padding: '2px' }} />
        <Select
          variant="calendar-header"
          options={YearOptions}
          key="year"
          name="year"
          value={yearValue}
          placeholder={yearValue}
          onBlur={() => {}}
          onChange={(e) => handleChange(e)}
        />
      </div>
    </div>
  );
};

const CalendarHeaderMemo = memo(CalendarHeader);
export { CalendarHeaderMemo as CalendarHeader };
